/**
 * useDistrictSummary — loads district-level stats + activity feed.
 *
 * Stats run in parallel with Promise.allSettled — any single failure degrades
 * gracefully. Extended to include totalFamilies, familiesRecentlyVisited (< 90
 * days), baptismsThisYear, and notesThisMonth for the editorial context panel.
 */

import { useEffect, useState } from "react";
import {
  getChurches,
  getVisits,
  getFieldEvents,
  getChurchEventsByDistrict,
  getPrayers,
  getFamiliesWithLastVisit,
  getBaptismRecords,
  getMaterials,
  getPreachingRosters,
} from "@/lib/api";
import { getLocalDateString } from "@/lib/date";
import { notesStorage } from "@/lib/notes-storage";
import { buildActivityFeed } from "@/features/activity/engine/buildActivityFeed";

function normalizeTitle(title = "") {
  return title.trim().toLowerCase().replace(/\s+/g, " ");
}
const SOURCE_PRIORITY = { campo: 3, distrito: 2, iglesia: 1 };

const EMPTY = {
  churchCount:             null,
  pendingVisits:           null,
  activePrayers:           null,
  nextEventPrimary:        null,
  nextEventSecondary:      null,
  totalFamilies:           null,
  totalPersons:            null,
  familiesRecentlyVisited: null,
  baptismsThisYear:        null,
  notesThisMonth:          null,
  districtMaterialValue:   null,
  materialsCount:          null,
  preachingRole:           null,
  recentActivity:          [],
};

// Module-level snapshot: returning to District is instantaneous on repeat visits.
const _districtCache = new Map();

export function useDistrictSummary(districtId) {
  const [summary, setSummary] = useState(() => _districtCache.get(districtId) ?? EMPTY);
  const [loading, setLoading] = useState(() => !_districtCache.has(districtId));

  useEffect(() => {
    if (!districtId) return;
    let cancelled = false;
    setLoading(true);

    const today        = getLocalDateString();
    const currentYear  = new Date().getFullYear().toString();
    const currentMonth = new Date().toISOString().slice(0, 7);

    Promise.allSettled([
      getChurches(districtId),
      getVisits({ district_id: districtId, status: "pendiente" }),
      getFieldEvents({ district_id: districtId, from_date: today }),
      getChurchEventsByDistrict({ district_id: districtId, from_date: today }),
      getPrayers({ district_id: districtId, status: "activa" }),
      notesStorage.getAll(districtId),
      getFamiliesWithLastVisit(districtId),
      getBaptismRecords({ district_id: districtId }),
      getMaterials({ district_id: districtId }),
      getPreachingRosters(districtId),
    ]).then(([churchRes, visitRes, fieldRes, churchEvRes, prayerRes, notesRes, familiesRes, baptismsRes, materialsRes, rostersRes]) => {
      if (cancelled) return;

      const churches  = churchRes.status   === "fulfilled" ? churchRes.value   || [] : [];
      const visits    = visitRes.status    === "fulfilled" ? visitRes.value    || [] : [];
      const field     = fieldRes.status    === "fulfilled" ? fieldRes.value    || [] : [];
      const churchEv  = churchEvRes.status === "fulfilled" ? churchEvRes.value || [] : [];
      const prayers   = prayerRes.status   === "fulfilled" ? prayerRes.value   || [] : [];
      const notes     = notesRes.status    === "fulfilled" ? notesRes.value    || [] : [];
      const families  = familiesRes.status === "fulfilled" ? familiesRes.value || [] : [];
      const baptisms  = baptismsRes.status  === "fulfilled" ? baptismsRes.value  || [] : [];
      const materials = materialsRes.status === "fulfilled" ? materialsRes.value || [] : [];
      const rosters   = rostersRes.status   === "fulfilled" ? rostersRes.value   || [] : [];

      const normalizedField  = field.map(e => ({ ...e, source: "campo" }));
      const normalizedChurch = churchEv.map(e => ({ ...e, source: "iglesia" }));
      const eventMap = new Map();
      for (const event of [...normalizedField, ...normalizedChurch]) {
        const key = `${normalizeTitle(event.title)}-${event.date}`;
        if (!eventMap.has(key)) {
          eventMap.set(key, event);
          continue;
        }
        const existing = eventMap.get(key);
        if ((SOURCE_PRIORITY[event.source] ?? 0) > (SOURCE_PRIORITY[existing.source] ?? 0)) {
          eventMap.set(key, event);
        }
      }
      const futureEvents = [...eventMap.values()]
        .sort((a, b) => (a.date || "").localeCompare(b.date || ""));
      const nextEventPrimary   = futureEvents[0] ?? null;
      const nextEventSecondary = futureEvents[1] ?? null;

      const pastEvents = [...field, ...churchEv].filter((e) => e.date <= today);
      const recentActivity = buildActivityFeed(
        { notes, visits, prayers, events: pastEvents },
        { limit: 5 }
      );

      const totalFamilies           = families.length;
      const totalPersons            = families.reduce((s, f) => s + (f.people_count ?? f.member_count ?? 0), 0);
      const familiesRecentlyVisited = families.filter((f) => (f.days_since ?? 999) < 90).length;
      const baptismsThisYear        = baptisms.filter((b) => (b.baptism_date || "").startsWith(currentYear)).length;
      const notesThisMonth          = notes.filter((n) => ((n.updated_locally_at || n.created_at) || "").startsWith(currentMonth)).length;

      const districtMaterialValue = materials.reduce((sum, m) => {
        const qty  = Array.isArray(m.material_stock) ? m.material_stock.reduce((s, st) => s + Number(st.quantity ?? 0), 0) : 0;
        const cost = Number(m.unit_cost ?? 0);
        return sum + qty * cost;
      }, 0);
      const materialsCount = materials.length;

      const activeRoster = rosters.find(r => r.date_start <= today && (r.date_end ?? "") >= today);
      const preachingRole = activeRoster
        ? { daysLeft: Math.max(0, Math.ceil((new Date(activeRoster.date_end + "T00:00:00") - new Date()) / 86400000)) }
        : null;

      const newSummary = {
        churchCount:             churches.length,
        pendingVisits:           visits.length,
        activePrayers:           prayers.length,
        nextEventPrimary,
        nextEventSecondary,
        totalFamilies,
        totalPersons:            totalPersons > 0 ? totalPersons : null,
        familiesRecentlyVisited,
        baptismsThisYear,
        notesThisMonth,
        districtMaterialValue,
        materialsCount,
        preachingRole,
        recentActivity,
      };
      _districtCache.set(districtId, newSummary);
      setSummary(newSummary);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [districtId]);

  return { summary, loading };
}
